// File: frontend/src/components/ui/ConfirmDialog.js
//
// The one confirmation dialog. Generalised from the local ConfirmDialog in
// Vouchers.js and the hand-rolled confirm dialogs elsewhere (e.g. SuperDashboard),
// so every irreversible/financial action in the app is confirmed the SAME way.
//
// Defaults to the `danger` intent because that is the case that matters most in a
// money app (delete / void / reverse / suspend). The tinted icon badge is fully
// theme-driven (light + dark) — no hardcoded colours. Pass intent="primary" for a
// benign confirmation, or icon={null} to hide the badge.
//
//   <ConfirmDialog
//     open={open}
//     title="Delete voucher batch?"
//     message="This permanently removes 200 unused vouchers. This cannot be undone."
//     confirmLabel="Delete"
//     loading={busy}
//     onConfirm={handleDelete}
//     onCancel={() => setOpen(false)}
//   />

import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, Box, useTheme, alpha,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AppButton from "./AppButton";

const TONE = {
  danger:  (t) => t.palette.error.main,
  primary: (t) => t.palette.primary.main,
  warning: (t) => t.palette.warning.main,
  info:    (t) => t.palette.info.main,
};

export default function ConfirmDialog({
  open,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  intent = "danger",
  loading = false,
  icon,
  onConfirm,
  onCancel,
}) {
  const theme = useTheme();
  const tone = (TONE[intent] || TONE.danger)(theme);
  // AppButton only knows primary/secondary/danger/ghost — map the alert tones.
  const buttonIntent = intent === "danger" ? "danger" : "primary";
  const showIcon = icon !== null;

  return (
    <Dialog
      open={!!open}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          {showIcon && (
            <Box sx={{
              width: 34, height: 34, borderRadius: 2, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              bgcolor: alpha(tone, 0.12), color: tone,
            }}>
              {icon || <WarningAmberRoundedIcon sx={{ fontSize: 19 }} />}
            </Box>
          )}
          <Box component="span">{title}</Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {typeof message === "string"
          ? <DialogContentText sx={{ color: "text.secondary" }}>{message}</DialogContentText>
          : message}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading} color="inherit">
          {cancelLabel}
        </Button>
        <AppButton intent={buttonIntent} onClick={onConfirm} disabled={loading} autoFocus>
          {loading ? "Working…" : confirmLabel}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}
